/**
 * Growth → Validation — the ACT Target-Acquisition tool (ADR-114 §5).
 *
 * Two steps with a MANUAL GATE between them:
 *   Step 1 — harvest a cohort (sector + location + target count), review it, and re-run
 *            with changed criteria as many times as needed.
 *   Step 2 — run the ACT T1–T4 scoring pass over the approved cohort.
 *
 * ⭐ THE GATE IS NOT A UI COURTESY. Scoring spends real money (Serper credits per
 * candidate, per query). The server refuses to score an unapproved cohort, so this screen
 * renders the gate rather than implementing it — pressing Approve is the operator
 * committing to the spend, which is why the cost preview sits next to the button.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActionConfirmModal } from './ActionConfirmModal';
import { FilterSearch } from '../../components/FilterSearch';
import { FilterPills, type FilterPillGroup } from '../../components/FilterPills';
import { useToast, toastFromError } from '../../components/ToastProvider';
import {
  listCohorts, getCohort, harvestCohort, approveCohort, scoreNextCandidate, getCostPreview,
  deleteCohort, foundVsRequested, verdictLabel, verdictTone, pathLabel, groupLabel,
  groupTier, tierTrack, GROUP_MAX, PASS_MARK,
  reclassifyCandidate, rejectCandidate, removeCandidate, sizeLabel, sizeRank,
  t1AutoPassRate,
  type Cohort, type Candidate, type CostPreview, type SectorKind, type Verdict,
  type CohortStatus,
} from '../../lib/growth-api';
import {
  HARVESTABLE_COUNTRIES, countryDef, composeLocation, jurisdictionFor,
  MAX_TARGET_COUNT, hasSubRegions, subRegionLabel, subRegionsFor, type CountryCode,
} from '../../lib/growth-regions';
import { InstantTime } from '../../lib/time-format';

/**
 * The curated sector list (ADR-114 §5 — "curated dropdown with a free-text override").
 *
 * ⭐ IT COVERS BOTH FOR-PROFIT AND NFP/CHARITY AS FIRST-CLASS CATEGORIES, and that is not
 * cosmetic: register-lookup logic differs by sector (ACNC vs ABN/ASIC), and today the AU
 * CHARITY register is keyless and WORKS while the for-profit one needs a key nobody has
 * provisioned. So an NFP cohort can reach a real verdict where a brokerage cohort cannot.
 */
const CURATED_SECTORS: { label: string; kind: SectorKind }[] = [
  { label: 'Mortgage and finance brokers', kind: 'for_profit' },
  { label: 'Accountants and bookkeepers', kind: 'for_profit' },
  { label: 'Real estate agencies', kind: 'for_profit' },
  { label: 'Legal practices', kind: 'for_profit' },
  { label: 'Dental and medical practices', kind: 'for_profit' },
  { label: 'Trades and home services', kind: 'for_profit' },
  { label: 'Allied health', kind: 'for_profit' },
  { label: 'Hospitality and cafés', kind: 'for_profit' },
  { label: 'Marketing and creative agencies', kind: 'for_profit' },
  { label: 'IT and managed services', kind: 'for_profit' },
  { label: 'Charities and not-for-profits', kind: 'nfp_charity' },
  { label: 'Community and sporting clubs', kind: 'nfp_charity' },
  { label: 'Religious organisations', kind: 'nfp_charity' },
  { label: 'Aged care and disability services', kind: 'nfp_charity' },
];

type SortKey = 'businessName' | 'verdict' | 't1Score' | 't2Score' | 'emailScore' | 'path' | 'size';
interface SortState { key: SortKey; dir: 'asc' | 'desc' }

const VERDICT_ORDER: Record<Verdict, number> = {
  pass: 0, escalate: 1, fail_no_data: 2, reject: 3, unscored: 4,
};

export function GrowthValidationTab() {
  const toast = useToast();

  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [activeCohort, setActiveCohort] = useState<Cohort | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [costPreview, setCostPreview] = useState<CostPreview | null>(null);

  const [loadingList, setLoadingList] = useState(true);
  const [harvesting, setHarvesting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [loadingCohort, setLoadingCohort] = useState(false);

  // Step 1 form.
  const [sectorChoice, setSectorChoice] = useState(CURATED_SECTORS[0].label);
  const [freeTextSector, setFreeTextSector] = useState('');
  const [useFreeText, setUseFreeText] = useState(false);
  const [freeTextKind, setFreeTextKind] = useState<SectorKind>('for_profit');
  // Location (UAT #4) — country drives the region list AND the register the scoring
  // engine consults, so the two fields move together.
  const [country, setCountry] = useState<CountryCode>('AU');
  const [region, setRegion] = useState('South Australia');
  /**
   * County (US) / Suburb (elsewhere) — D-009's third level of harvest granularity.
   * Empty string means "the whole state", which stays the default: a state-level harvest
   * is still the right first move when an operator does not yet know where the sector
   * concentrates.
   */
  const [subRegion, setSubRegion] = useState('');
  const [freeTextLocation, setFreeTextLocation] = useState('');
  const [useFreeLocation, setUseFreeLocation] = useState(false);
  const [targetCount, setTargetCount] = useState(10);

  // Scoring progress (UAT #5) — the endpoint scores ONE candidate per call, so the client
  // drives the loop and can report "3 of 10" honestly instead of an indeterminate bar.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Every mutate goes through ActionConfirmModal (component library §4) — deleting a
  // cohort discards a real harvest and the Serper spend behind it, so it is not a
  // single-click action.
  const [confirmDelete, setConfirmDelete] = useState<Cohort | null>(null);
  // ADR-114 §7 per-candidate actions.
  const [reclassifyFor, setReclassifyFor] = useState<Candidate | null>(null);
  const [reclassifySector, setReclassifySector] = useState('');
  const [rejectFor, setRejectFor] = useState<Candidate | null>(null);
  const [rejectTarget, setRejectTarget] = useState<'business' | 'email'>('business');
  const [actionBusy, setActionBusy] = useState(false);

  // Cohort-list controls (UAT #5) — the documented admin composition.
  const [cohortSearch, setCohortSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<CohortStatus>>(
    new Set(['harvested', 'approved', 'scoring', 'scored']),
  );
  const [cohortSort, setCohortSort] = useState<{ key: 'label' | 'createdAt' | 'status'; dir: 'asc' | 'desc' }>(
    { key: 'createdAt', dir: 'desc' },
  );

  // Results table controls.
  const [search, setSearch] = useState('');
  const [verdictFilter, setVerdictFilter] = useState<Set<Verdict>>(
    new Set(['pass', 'escalate', 'reject', 'fail_no_data', 'unscored']),
  );
  const [sort, setSort] = useState<SortState>({ key: 'verdict', dir: 'asc' });
  const [expanded, setExpanded] = useState<string | null>(null);

  const refreshCohorts = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await listCohorts();
      setCohorts(res.cohorts);
    } catch (err) {
      const t = toastFromError(err, 'Could not load cohorts');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setLoadingList(false);
    }
  }, [toast]);

  useEffect(() => { void refreshCohorts(); }, [refreshCohorts]);

  const openCohort = useCallback(async (cohortId: string) => {
    setLoadingCohort(true);
    setExpanded(null);
    try {
      const [detail, preview] = await Promise.all([
        getCohort(cohortId),
        getCostPreview(cohortId).catch(() => null),
      ]);
      setActiveCohort(detail.cohort);
      setCandidates(detail.candidates);
      setCostPreview(preview);
    } catch (err) {
      const t = toastFromError(err, 'Could not open cohort');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setLoadingCohort(false);
    }
  }, [toast]);

  /**
   * Why the count is validated here rather than left to `<input max>`: the attribute
   * constrains the spinner arrows only, so a TYPED value posts unchanged. An operator
   * typed 100, the API refused it correctly, and the refusal reached them as "Something
   * went wrong. Please try again in a moment." — a well-formed request that looked like
   * an outage (13 September 2026). The server still refuses it (that is the check that
   * holds); this one just means the operator never has to spend a round-trip to find out.
   */
  const targetCountError = !Number.isInteger(targetCount) || targetCount < 1
    ? 'Enter a whole number of records — at least 1.'
    : targetCount > MAX_TARGET_COUNT
      ? `Cohorts are capped at ${MAX_TARGET_COUNT} candidates per request.`
      : null;

  const onHarvest = async () => {
    const sector = useFreeText ? freeTextSector.trim() : sectorChoice;
    const sectorKind = useFreeText
      ? freeTextKind
      : CURATED_SECTORS.find((s) => s.label === sectorChoice)?.kind ?? 'for_profit';
    const location = composeLocation(
      useFreeLocation ? freeTextLocation : region, country, useFreeLocation, subRegion,
    );
    if (!sector || !location) {
      toast.push({ variant: 'error', title: 'Sector and location are required' });
      return;
    }
    setHarvesting(true);
    try {
      const res = await harvestCohort({
        sector, sectorKind, location,
        // ⭐ SENT ALONGSIDE `location`, NOT PARSED BACK OUT OF IT. The register lookup needs
        // a state, and once a county is in the location string it cannot be recovered
        // safely — Utah has a Washington county. Omitted for a free-typed location, where
        // there is no selected region to send.
        ...(useFreeLocation ? {} : { region }),
        jurisdiction: jurisdictionFor(country),
        targetCount,
      });
      // ⭐ The shortfall is stated out loud, in the toast as well as the panel. A quietly
      // short list reads as "that's all there is" when it may mean the criteria were bad.
      toast.push({
        variant: res.shortfall > 0 ? 'info' : 'success',
        title: `Harvested ${foundVsRequested(res.cohort)}`,
        detail: res.shortfall > 0
          ? `${res.shortfall} short of the target across ${res.queriesRun} searches. `
            + 'Widen the sector or location and run Step 1 again before approving.'
          : `Found the full target across ${res.queriesRun} searches.`,
      });
      await refreshCohorts();
      await openCohort(res.cohort.cohortId);
    } catch (err) {
      const t = toastFromError(err, 'Harvest failed');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setHarvesting(false);
    }
  };

  const onApprove = async () => {
    if (!activeCohort) return;
    setApproving(true);
    try {
      await approveCohort(activeCohort.cohortId);
      toast.push({
        variant: 'success', title: 'Cohort approved',
        detail: 'Step 2 scoring can now run against it.',
      });
      await openCohort(activeCohort.cohortId);
      await refreshCohorts();
    } catch (err) {
      const t = toastFromError(err, 'Could not approve cohort');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setApproving(false);
    }
  };

  /**
   * Step 2 — drive the scoring loop.
   *
   * ⭐⭐ ONE REQUEST PER CANDIDATE, IN A CLIENT LOOP. A candidate takes 20–25s against the
   * real sources (measured), and API Gateway's integration timeout is a hard 30s — so the
   * old whole-cohort POST could never return to a browser: it 504'd while the Lambda ran
   * on invisibly. Looping per candidate keeps every request inside the window, and the
   * step response carries `scored`/`total` so the operator sees "3 of 10" rather than an
   * indeterminate bar (UAT #5).
   *
   * The loop is RESUMABLE by construction: each candidate is persisted as it completes, so
   * a reload or a dropped connection costs one candidate, and pressing the button again
   * picks up from the first unscored one.
   */
  const onScore = async () => {
    if (!activeCohort) return;
    setScoring(true);
    setProgress(null);
    try {
      let guard = 0;
      for (;;) {
        // A hard ceiling so a server that never reports `complete` cannot spin forever.
        // Cohorts cap at 50; 60 iterations is slack without being unbounded.
        if (guard++ > 60) throw new Error('scoring did not complete within the expected number of steps');

        let step;
        try {
          step = await scoreNextCandidate(activeCohort.cohortId);
        } catch (err) {
          // ⭐⭐ A TIMEOUT HERE IS NOT A FAILED CANDIDATE — IT IS A SLOW ONE.
          //
          // API Gateway's integration timeout is a hard 30s, and a candidate's scoring
          // time varies with how fast eight third parties answer (measured 13s–63s before
          // the Wayback budget cap). When the gateway hangs up, THE LAMBDA KEEPS RUNNING
          // and persists the candidate — so the work is done, and only the response was
          // lost. Treating that as a fatal error would abandon a run that is actually
          // progressing, and would tell the operator nothing was scored when it was.
          //
          // So: on a timeout, loop again. The server always serves the NEXT UNSCORED
          // candidate, so the retry either picks up the one that just landed or moves on.
          const status = (err as { status?: number } | null)?.status;
          const timedOut = status === 504 || status === 0;
          if (!timedOut) throw err;
          // Re-read progress from the server rather than guessing it locally.
          const snapshot = await getCohort(activeCohort.cohortId);
          const done = snapshot.candidates.filter((c) => c.verdict !== 'unscored').length;
          setProgress({ done, total: snapshot.candidates.length });
          setCandidates(snapshot.candidates);
          if (done >= snapshot.candidates.length) break;
          continue;
        }
        setProgress({ done: step.scored, total: step.total });

        // Merge this candidate into the table as it lands, so results appear progressively
        // rather than all at the end.
        if (step.candidate) {
          const scored = step.candidate;
          setCandidates((prev) => prev.map((c) =>
            c.candidateId === scored.candidateId ? scored : c));
        }
        if (step.complete) break;
      }

      const refreshed = await getCohort(activeCohort.cohortId);
      setActiveCohort(refreshed.cohort);
      setCandidates(refreshed.candidates);

      const gaps = refreshed.candidates.filter((c) => c.verdict === 'fail_no_data').length;
      toast.push({
        variant: 'success',
        title: `Scored ${refreshed.candidates.length} candidates`,
        detail: gaps > 0
          ? `${gaps} could not be scored — open a row to see which checks had no data.`
          : undefined,
      });
      await refreshCohorts();
    } catch (err) {
      const t = toastFromError(err, 'Scoring failed');
      // ⚠ Say what SURVIVED. Because each candidate is persisted as it completes, a failure
      // partway through is not a lost run — telling the operator only "scoring failed"
      // would invite them to start over and pay for the work already done.
      toast.push({
        variant: 'error',
        title: t.title,
        detail: progress
          ? `${t.detail} — ${progress.done} of ${progress.total} were scored and saved; press Run again to continue.`
          : t.detail,
      });
      try {
        const refreshed = await getCohort(activeCohort.cohortId);
        setActiveCohort(refreshed.cohort);
        setCandidates(refreshed.candidates);
      } catch { /* the toast above already carries the failure */ }
    } finally {
      setScoring(false);
      setProgress(null);
    }
  };

  const refreshActiveCohort = useCallback(async () => {
    if (!activeCohort) return;
    const detail = await getCohort(activeCohort.cohortId);
    setActiveCohort(detail.cohort);
    setCandidates(detail.candidates);
  }, [activeCohort]);

  const onRemoveCandidate = async (c: Candidate) => {
    if (!activeCohort) return;
    setActionBusy(true);
    try {
      await removeCandidate(activeCohort.cohortId, c.candidateId);
      toast.push({ variant: 'success', title: 'Removed from cohort', detail: c.businessName });
      await refreshActiveCohort();
    } catch (err) {
      const t = toastFromError(err, 'Could not remove the candidate');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally { setActionBusy(false); }
  };

  const onConfirmReclassify = async () => {
    if (!activeCohort || !reclassifyFor || !reclassifySector.trim()) return;
    setActionBusy(true);
    try {
      await reclassifyCandidate(activeCohort.cohortId, reclassifyFor.candidateId, reclassifySector.trim());
      toast.push({
        variant: 'success', title: 'Sector reassigned',
        detail: `${reclassifyFor.businessName} → ${reclassifySector.trim()}`,
      });
      setReclassifyFor(null);
      await refreshActiveCohort();
    } catch (err) {
      const t = toastFromError(err, 'Could not reassign the sector');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally { setActionBusy(false); }
  };

  // Reset to the always-available target each time the dialog opens, so a previous
  // email-flag choice cannot carry over onto an unscored candidate.
  useEffect(() => { if (rejectFor) setRejectTarget('business'); }, [rejectFor]);
  useEffect(() => { if (reclassifyFor) setReclassifySector(reclassifyFor.sector ?? ''); }, [reclassifyFor]);

  const onConfirmReject = async () => {
    if (!activeCohort || !rejectFor) return;
    setActionBusy(true);
    try {
      await rejectCandidate(activeCohort.cohortId, rejectFor.candidateId, rejectTarget);
      toast.push({
        variant: 'success',
        title: rejectTarget === 'email' ? 'Email flagged as suspect' : 'Business rejected',
        // Say what it MEANS — a registry entry is permanent and checked first, so this is
        // not a per-cohort action and the operator should know that before confirming.
        detail: rejectTarget === 'email'
          ? `${rejectFor.domain} recorded in the suspect registry.`
          : `${rejectFor.businessName} recorded in the suspect registry — it will be screened out of future cohorts.`,
      });
      setRejectFor(null);
      await refreshActiveCohort();
    } catch (err) {
      const t = toastFromError(err, 'Could not record the rejection');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally { setActionBusy(false); }
  };

  /** Remove a cohort and its candidates (UAT #5). */
  const onDeleteCohort = async (c: Cohort) => {
    setDeletingId(c.cohortId);
    setConfirmDelete(null);
    try {
      await deleteCohort(c.cohortId);
      toast.push({ variant: 'success', title: 'Cohort deleted', detail: c.label });
      if (activeCohort?.cohortId === c.cohortId) {
        setActiveCohort(null);
        setCandidates([]);
        setCostPreview(null);
      }
      await refreshCohorts();
    } catch (err) {
      const t = toastFromError(err, 'Could not delete cohort');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Cohort list: the same documented composition as the results table (UAT #5) ──
  const cohortStatusGroups: FilterPillGroup<CohortStatus>[] = useMemo(() => {
    const count = (st: CohortStatus) => cohorts.filter((c) => c.status === st).length;
    return (['harvested', 'approved', 'scoring', 'scored'] as CohortStatus[])
      .map((st) => ({ key: st, label: statusLabel(st), count: count(st) }));
  }, [cohorts]);

  const visibleCohorts = useMemo(() => {
    const q = cohortSearch.trim().toLowerCase();
    const rows = cohorts.filter((c) => {
      if (!statusFilter.has(c.status)) return false;
      if (!q) return true;
      return c.label.toLowerCase().includes(q)
        || c.sector.toLowerCase().includes(q)
        || c.location.toLowerCase().includes(q);
    });
    const dir = cohortSort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (cohortSort.key) {
        case 'label': return a.label.localeCompare(b.label) * dir;
        case 'status': return a.status.localeCompare(b.status) * dir;
        default: return a.createdAt.localeCompare(b.createdAt) * dir;
      }
    });
  }, [cohorts, cohortSearch, statusFilter, cohortSort]);

  /** The pilot metric, recomputed from the loaded candidates. */
  const t1Rollup = useMemo(() => t1AutoPassRate(candidates), [candidates]);

  const toggleCohortSort = (key: 'label' | 'createdAt' | 'status') =>
    setCohortSort((sv) => (sv.key === key
      ? { key, dir: sv.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));

  const cohortSortIndicator = (key: 'label' | 'createdAt' | 'status') =>
    (cohortSort.key === key ? (cohortSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // ── Results table: search + filter + sort, the documented admin composition ──
  const verdictGroups: FilterPillGroup<Verdict>[] = useMemo(() => {
    const count = (v: Verdict) => candidates.filter((c) => c.verdict === v).length;
    return (['pass', 'escalate', 'fail_no_data', 'reject', 'unscored'] as Verdict[])
      .map((v) => ({ key: v, label: verdictLabel(v), count: count(v) }));
  }, [candidates]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = candidates.filter((c) => {
      if (!verdictFilter.has(c.verdict)) return false;
      if (!q) return true;
      return c.businessName.toLowerCase().includes(q) || c.domain.toLowerCase().includes(q);
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sort.key) {
        case 'verdict':
          return (VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict]) * dir;
        case 't1Score':
          return ((a.t1Score ?? -1) - (b.t1Score ?? -1)) * dir;
        case 't2Score':
          // A skipped T2 sorts BELOW any real T2 score rather than as zero — it was not
          // a poor result, it was never bought.
          return ((a.t2Score ?? -1) - (b.t2Score ?? -1)) * dir;
        case 'emailScore':
          return ((a.emailScore ?? -1) - (b.emailScore ?? -1)) * dir;
        case 'path':
          return (pathLabel(a.path).localeCompare(pathLabel(b.path))) * dir;
        case 'size':
          // `unknown` sorts LAST rather than as zero — an unmeasured business is not a
          // tiny one, and ranking it alongside micro would mislead the operator.
          return (sizeRank(a.sizeBand) - sizeRank(b.sizeBand)) * dir;
        default:
          return a.businessName.localeCompare(b.businessName) * dir;
      }
    });
  }, [candidates, search, verdictFilter, sort]);

  const toggleSort = (key: SortKey) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const sortIndicator = (key: SortKey) =>
    sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="growth-validation">
      {/* ── Step 1 ─────────────────────────────────────────────────────────── */}
      <section className="growth-step" aria-labelledby="growth-step1">
        <h3 id="growth-step1" className="growth-step-title">Step 1 — Harvest a cohort</h3>
        <p className="growth-step-help">
          Search for organisations matching a sector and location. Review what comes back,
          change the criteria and run again as many times as you need. Nothing is scored —
          and nothing is charged — until you approve the cohort in Step&nbsp;2.
        </p>

        <div className="growth-form">
          <div className="growth-field">
            {/* A <div>, not a <label>: this field contains its own override <label>, and a
                nested label breaks the accessible name of BOTH controls. */}
            <span className="growth-field-label">Sector</span>
            {useFreeText ? (
              <input
                type="text"
                value={freeTextSector}
                onChange={(e) => setFreeTextSector(e.target.value)}
                placeholder="e.g. marine surveyors"
                aria-label="Sector (free text)"
              />
            ) : (
              <select
                value={sectorChoice}
                onChange={(e) => setSectorChoice(e.target.value)}
                aria-label="Sector"
              >
                {CURATED_SECTORS.map((s) => (
                  <option key={s.label} value={s.label}>{s.label}</option>
                ))}
              </select>
            )}
            <label className="growth-inline-toggle">
              <input
                type="checkbox"
                checked={useFreeText}
                onChange={(e) => setUseFreeText(e.target.checked)}
              />
              {/* The override exists so an uncovered sector is never boxed out — but the
                  curated list produces cleaner searches, so it stays the default. */}
              <span>Type a sector not in the list</span>
            </label>
            {useFreeText && (
              <select
                value={freeTextKind}
                onChange={(e) => setFreeTextKind(e.target.value as SectorKind)}
                aria-label="Sector type"
              >
                <option value="for_profit">For-profit business</option>
                <option value="nfp_charity">Charity or not-for-profit</option>
              </select>
            )}
          </div>

          <div className="growth-field">
            <span className="growth-field-label">Country</span>
            <select
              value={country}
              onChange={(e) => {
                const next = e.target.value as CountryCode;
                setCountry(next);
                // Reset the region — a state from the previous country is never valid for
                // the new one, and leaving it would silently harvest the wrong place.
                setRegion(countryDef(next).regions[0]);
                // ⭐ AND the sub-region — a county from the previous country/state is never
                // valid for the new one, and leaving it would compose a location naming a
                // county that does not exist in the state beside it.
                setSubRegion('');
              }}
              aria-label="Country"
            >
              {/* Derived from the `harvestable` flag, never hand-listed — a country is
                  offered only once the Lambda's honeypot lists cover it and a live run
                  has proven so. See growth-regions.ts ⛔. */}
              {HARVESTABLE_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            {!countryDef(country).registerSupported && (
              // Said up front rather than discovered a cohort later: harvesting works, but
              // the business-register anchor (50 of 178 points) has no adapter here.
              <span className="growth-field-warning">
                No business-register lookup for {countryDef(country).label} yet — candidates
                will report the register check as “no data”.
              </span>
            )}
          </div>

          <div className="growth-field">
            <span className="growth-field-label">
              {useFreeLocation ? 'Location' : countryDef(country).regionLabel}
            </span>
            {useFreeLocation ? (
              <input
                type="text"
                value={freeTextLocation}
                onChange={(e) => setFreeTextLocation(e.target.value)}
                placeholder="e.g. Adelaide CBD, South Australia"
                aria-label="Location (free text)"
                aria-describedby="growth-free-location-hint"
              />
            ) : (
              <select
                value={region}
                onChange={(e) => { setRegion(e.target.value); setSubRegion(''); }}
                aria-label={countryDef(country).regionLabel}
              >
                {countryDef(country).regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            )}
            {useFreeLocation && (
              // ⛔ THE COUNTRY ABOVE STILL APPLIES, AND SAYING SO IS THE POINT. A free-typed
              // location does NOT carry a country with it, so "Texas, United States" typed
              // here with the country left on Australia used to harvest the US with
              // Australian search parameters and honeypot lists. The API now refuses that
              // contradiction outright (`location_jurisdiction_mismatch`); this line is so
              // the operator does not have to discover the rule by tripping it.
              <span className="growth-field-hint" id="growth-free-location-hint">
                The country above still applies — it selects the search region and the
                business register, so a location outside {countryDef(country).label} will
                be refused.
              </span>
            )}
            {/* Mirrors the sector override exactly — same control, same wording shape. */}
            <label className="growth-inline-toggle">
              <input
                type="checkbox"
                checked={useFreeLocation}
                onChange={(e) => setUseFreeLocation(e.target.checked)}
              />
              <span>Type a location not in the list</span>
            </label>
          </div>

          {/*
            ⭐⭐ THE THIRD LEVEL — County (US) / Suburb (elsewhere), D-009.
            Shown ONLY where seed data exists, and never for a free-typed location (which
            carries its own granularity already). A state-level harvest is too coarse to be
            a targeting decision: Harris County alone is 109,874 employer establishments.
            ⛔ Absent seed data is NOT "this state has no counties" — it is "we have no
            snapshot for it yet", so the field hides rather than showing an empty dropdown.
            The live Census tool (`lambdas/growth/src/census.ts`) is what removes the
            ten-state ceiling; this list is a synchronous convenience, not the source of
            truth for any figure.
          */}
          {!useFreeLocation && hasSubRegions(country, region) && (
            <div className="growth-field">
              <span className="growth-field-label">
                {subRegionLabel(country)} <span className="growth-field-optional">(optional)</span>
              </span>
              <select
                value={subRegion}
                onChange={(e) => setSubRegion(e.target.value)}
                aria-label={`${subRegionLabel(country)} (optional)`}
              >
                <option value="">All of {region}</option>
                {subRegionsFor(country, region).map((c) => (
                  <option key={c.fips} value={c.name}>
                    {c.name} — {c.estab.toLocaleString()} businesses
                  </option>
                ))}
              </select>
              <span className="growth-field-hint">
                Narrows the search to one {subRegionLabel(country).toLowerCase()}. Counts are
                US Census County Business Patterns 2022 employer establishments — a guide to
                where the volume is, not a live figure.
              </span>
            </div>
          )}

          <div className="growth-field growth-field-narrow">
            <span className="growth-field-label">How many</span>
            <input
              type="number"
              min={1}
              max={MAX_TARGET_COUNT}
              value={targetCount}
              onChange={(e) => setTargetCount(Number(e.target.value))}
              aria-label="Target record count"
              aria-invalid={targetCountError ? true : undefined}
            />
            {targetCountError && (
              <span className="growth-field-warning" role="alert">
                {targetCountError}
              </span>
            )}
          </div>

          <button
            type="button"
            className="growth-primary"
            onClick={() => void onHarvest()}
            disabled={harvesting || targetCountError !== null}
          >
            {harvesting ? 'Searching…' : 'Harvest cohort'}
          </button>
        </div>

        {harvesting && (
          // Mandatory loading indicator — skeleton + text in tandem, per the standing
          // directive. A page that renders bare while data is in flight is a violation.
          <div className="growth-loading" role="status">
            <div className="growth-skeleton" aria-hidden="true" />
            <span>Searching for organisations — this runs several searches and can take a moment.</span>
          </div>
        )}
      </section>

      {/* ── Cohorts ────────────────────────────────────────────────────────── */}
      <section className="growth-step" aria-labelledby="growth-cohorts">
        <div className="growth-section-head">
          <h3 id="growth-cohorts" className="growth-step-title">Cohorts</h3>
          {/* Refresh sits with the section it refreshes, as the icon-only action it is. */}
          <button
            type="button"
            className="portal-action-icon-btn"
            onClick={() => void refreshCohorts()}
            disabled={loadingList}
            aria-label="Refresh cohorts"
            title="Refresh cohorts"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>

        {loadingList ? (
          <div className="growth-loading" role="status">
            <div className="growth-skeleton" aria-hidden="true" />
            <span>Loading cohorts…</span>
          </div>
        ) : cohorts.length === 0 ? (
          <p className="growth-empty">No cohorts yet — harvest one above to get started.</p>
        ) : (
          <>
            <div className="growth-table-controls">
              <FilterSearch
                value={cohortSearch}
                onChange={setCohortSearch}
                placeholder="Search cohorts…"
                ariaLabel="Search cohorts"
              />
              <FilterPills
                groups={cohortStatusGroups}
                selected={statusFilter}
                onChange={setStatusFilter}
                ariaLabel="Filter cohorts by status"
              />
            </div>

            {visibleCohorts.length === 0 ? (
              <p className="growth-empty">
                No cohorts match the current filters — select a status above to see rows.
              </p>
            ) : (
              <div className="growth-table-scroll">
                <table className="growth-table">
                  <thead>
                    <tr>
                      <th>
                        <button type="button" className="growth-sort-btn" onClick={() => toggleCohortSort('label')}>
                          Cohort{cohortSortIndicator('label')}
                        </button>
                      </th>
                      <th>Found</th>
                      <th>
                        <button type="button" className="growth-sort-btn" onClick={() => toggleCohortSort('status')}>
                          Status{cohortSortIndicator('status')}
                        </button>
                      </th>
                      <th>
                        <button type="button" className="growth-sort-btn" onClick={() => toggleCohortSort('createdAt')}>
                          Created{cohortSortIndicator('createdAt')}
                        </button>
                      </th>
                      {/* The standard last-column icon-only action cell. */}
                      <th className="portal-actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCohorts.map((c) => (
                      <tr
                        key={c.cohortId}
                        className={activeCohort?.cohortId === c.cohortId ? 'growth-row-active' : undefined}
                      >
                        <td>
                          <div className="growth-cell-primary">{c.label}</div>
                          <div className="growth-cell-secondary">{c.location}</div>
                        </td>
                        <td>{foundVsRequested(c)}</td>
                        <td>
                          <span className="portal-badge growth-verdict growth-verdict-neutral">
                            {statusLabel(c.status)}
                          </span>
                        </td>
                        <td className="growth-cell-secondary">
                          <InstantTime iso={c.createdAt} />
                        </td>
                        <td className="portal-users-row-actions portal-actions-col">
                          {/* Measured-aligned icon row (34px buttons, 8px gap) — the
                              standard multi-action pattern (.portal-actions-row). */}
                          <div className="portal-actions-row">
                            <button
                              type="button"
                              className="portal-action-icon-btn"
                              onClick={() => void openCohort(c.cohortId)}
                              aria-label={`Open ${c.label}`}
                              title={`Open ${c.label}`}
                            >
                              {/* eye glyph — the standard "view" action */}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="portal-action-icon-btn portal-action-icon-btn-danger"
                              onClick={() => setConfirmDelete(c)}
                              disabled={deletingId === c.cohortId}
                              aria-label={`Delete ${c.label}`}
                              title={`Delete ${c.label}`}
                            >
                              {/* trash glyph — the standard "delete" action */}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Step 2 ─────────────────────────────────────────────────────────── */}
      {activeCohort && (
        <section className="growth-step" role="region" aria-labelledby="growth-step2">
          <h3 id="growth-step2" className="growth-step-title">Step 2 — Score the cohort</h3>

          <div className="growth-cohort-summary">
            <div>
              <strong>{activeCohort.label}</strong>
              <div className="growth-cohort-meta">
                {/* ⭐ Found-vs-requested, always as a pair. */}
                Found {foundVsRequested(activeCohort)} · {statusLabel(activeCohort.status)}
              </div>
            </div>

            {/*
              ⭐⭐ THE PILOT METRIC — share of candidates that cleared 100 on T1 ALONE.
              T1 is free, T2 is paid, so this percentage is what drives T2 volume and cost
              planning. It sits on the cohort header precisely so it is readable WITHOUT
              opening a single candidate row.
            */}
            {t1Rollup.scored > 0 && (
              <div className="growth-rollup" role="status">
                <div className="growth-rollup-figure">
                  {t1Rollup.pct}<span className="growth-rollup-pct">%</span>
                </div>
                <div className="growth-rollup-label">
                  <strong>passed on T1 alone</strong>
                  <div className="growth-cell-secondary">
                    {t1Rollup.passed} of {t1Rollup.scored} scored · no T2 spend
                  </div>
                  {t1Rollup.scored < candidates.length && (
                    /* Denominator is SCORED candidates. Say so while a run is in flight,
                       or the number looks like it is drifting. */
                    <div className="growth-cell-secondary">
                      {candidates.length - t1Rollup.scored} still to score
                    </div>
                  )}
                </div>
              </div>
            )}

            {costPreview && activeCohort.status === 'harvested' && (
              <div className="growth-cost">
                <div className="growth-cost-line">
                  <span>Estimated search cost</span>
                  <strong>
                    ${costPreview.totalUsd.toFixed(2)} USD
                    <span className="growth-cost-range">
                      {' '}({costPreview.serperQueriesMin}–{costPreview.serperQueriesMax} searches)
                    </span>
                  </strong>
                </div>
                {/*
                  ⭐ THE ASIC EXTRACT IS PRICED SEPARATELY AND IS NOT IN THE TOTAL.
                  ADR-114 §6 makes it an explicit opt-in, never a silent default — blending
                  $10/record into one figure would let an operator approve a $500 run
                  believing they had approved a $0.40 one.
                */}
                <div className="growth-cost-optin">
                  Director cross-match (ASIC extract) is <strong>not included</strong> — it
                  costs ${costPreview.optIn.asicExtractPerRecordUsd} per record
                  (${costPreview.optIn.asicExtractIfAllRecordsUsd} for this cohort) and is
                  opt-in only. It does not run in this pass.
                </div>
              </div>
            )}
          </div>

          <div className="growth-actions">
            {activeCohort.status === 'harvested' && (
              <>
                <button
                  type="button"
                  className="growth-primary"
                  onClick={() => void onApprove()}
                  disabled={approving}
                >
                  {approving ? 'Approving…' : 'Approve cohort for scoring'}
                </button>
                <span className="growth-actions-note">
                  Review the list below first. Approving commits to the search spend above.
                </span>
              </>
            )}
            {(activeCohort.status === 'approved' || activeCohort.status === 'scored') && (
              <button
                type="button"
                className="growth-primary"
                onClick={() => void onScore()}
                disabled={scoring}
              >
                {scoring ? 'Scoring…' : activeCohort.status === 'scored' ? 'Re-run scoring' : 'Run ACT scoring'}
              </button>
            )}
          </div>

          {(scoring || loadingCohort) && (
            <div className="growth-loading" role="status">
              <div className="growth-skeleton" aria-hidden="true" />
              <span>
                {scoring
                  ? progress
                    // ⭐ The granular count Jeff asked for. Each candidate takes 20–25s
                    // against eight live sources, so an indeterminate bar tells the
                    // operator nothing about whether to wait or intervene.
                    ? `Scoring ${progress.done} of ${progress.total} — about `
                      + `${Math.max(1, Math.round(((progress.total - progress.done) * 22) / 60))} min remaining`
                    : 'Starting the scoring run…'
                  : 'Loading cohort…'}
              </span>
            </div>
          )}

          {/* ── Results table ─────────────────────────────────────────────── */}
          {candidates.length > 0 && !loadingCohort && (
            <>
              <div className="growth-table-controls">
                <FilterSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Search business or domain…"
                  ariaLabel="Search candidates"
                />
                <FilterPills
                  groups={verdictGroups}
                  selected={verdictFilter}
                  onChange={setVerdictFilter}
                  ariaLabel="Filter candidates by outcome"
                />
              </div>

              {visible.length === 0 ? (
                <p className="growth-empty">
                  No candidates match the current filters — select an outcome above to see rows.
                </p>
              ) : (
                <div className="growth-table-scroll">
                  <table className="growth-table">
                    <thead>
                      <tr>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('businessName')}>
                            Business{sortIndicator('businessName')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('verdict')}>
                            Outcome{sortIndicator('verdict')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('t1Score')}>
                            T1 Score{sortIndicator('t1Score')}
                          </button>
                          <div className="growth-th-note">free tier · pass ≥ {PASS_MARK}</div>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('t2Score')}>
                            T2 Score{sortIndicator('t2Score')}
                          </button>
                          <div className="growth-th-note">paid tier · only if needed</div>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('emailScore')}>
                            Email ACT{sortIndicator('emailScore')}
                          </button>
                          <div className="growth-th-note">T3+T4 · pass ≥ {PASS_MARK}</div>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('path')}>
                            Contact path{sortIndicator('path')}
                          </button>
                        </th>
                        <th>
                          <button type="button" className="growth-sort-btn" onClick={() => toggleSort('size')}>
                            Size{sortIndicator('size')}
                          </button>
                          <div className="growth-th-note">inferred, rough</div>
                        </th>
                        <th className="portal-actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((c) => (
                        <CandidateRow
                          key={c.candidateId}
                          candidate={c}
                          expanded={expanded === c.candidateId}
                          onToggle={() => setExpanded(expanded === c.candidateId ? null : c.candidateId)}
                          onReclassify={setReclassifyFor}
                          onReject={setRejectFor}
                          onRemove={(cand) => void onRemoveCandidate(cand)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <ActionConfirmModal
        open={reclassifyFor !== null}
        title="Reassign this candidate's sector"
        intent="primary"
        primaryLabel="Reassign sector"
        primaryDisabled={!reclassifySector.trim()}
        loading={actionBusy}
        onConfirm={() => void onConfirmReclassify()}
        onCancel={() => setReclassifyFor(null)}
      >
        <p>
          <strong>{reclassifyFor?.businessName}</strong> was harvested under{' '}
          <strong>{reclassifyFor?.sector ?? 'its cohort sector'}</strong>.
        </p>
        {reclassifyFor?.aggregatorSuspected && (
          <p>
            {/* This is the documented correction path for a false aggregator flag (§7),
                so say so at the moment the operator is acting on one. */}
            It is flagged as a possible directory or comparison site. Reassigning it is how
            that flag gets corrected.
          </p>
        )}
        <label className="growth-field">
          <span className="growth-field-label">Correct sector</span>
          <input
            type="text"
            value={reclassifySector}
            onChange={(e) => setReclassifySector(e.target.value)}
            placeholder="e.g. Comparison and directory sites"
            aria-label="Correct sector"
          />
        </label>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={rejectFor !== null}
        title={rejectTarget === 'email' ? 'Flag this email as suspect?' : 'Reject this business?'}
        intent="danger"
        primaryLabel={rejectTarget === 'email' ? 'Flag email' : 'Reject business'}
        loading={actionBusy}
        onConfirm={() => void onConfirmReject()}
        onCancel={() => setRejectFor(null)}
      >
        <p><strong>{rejectFor?.businessName}</strong> — {rejectFor?.domain}</p>
        <p>
          {/* The registry is permanent and checked FIRST, so a reject is not a per-cohort
              action. The operator is told that before confirming, not after. */}
          This is recorded in the suspect registry, which is <strong>permanent</strong> and
          is checked before every future harvest — so this business will be screened out of
          later cohorts, not just this one.
        </p>
        <label className="growth-inline-toggle">
          <input
            type="radio"
            name="reject-target"
            checked={rejectTarget === 'business'}
            onChange={() => setRejectTarget('business')}
          />
          <span>Reject the business</span>
        </label>
        {/* ⚠ Step-2 only: before a scoring pass there is no extracted address to flag. The
            server returns 409 regardless — this only avoids offering an action that cannot
            succeed. */}
        {rejectFor?.verdict !== 'unscored' && (
          <label className="growth-inline-toggle">
            <input
              type="radio"
              name="reject-target"
              checked={rejectTarget === 'email'}
              onChange={() => setRejectTarget('email')}
            />
            <span>Flag the email address as suspect (keeps the business)</span>
          </label>
        )}
      </ActionConfirmModal>

      <ActionConfirmModal
        open={confirmDelete !== null}
        title="Delete this cohort?"
        intent="danger"
        primaryLabel="Delete cohort"
        loading={deletingId !== null}
        onConfirm={() => { if (confirmDelete) void onDeleteCohort(confirmDelete); }}
        onCancel={() => setConfirmDelete(null)}
      >
        <p>
          <strong>{confirmDelete?.label}</strong> and its{' '}
          {confirmDelete?.found ?? 0} candidate record(s) will be removed permanently.
        </p>
        <p>
          {/* Say what it costs to undo — the harvest was paid for. */}
          The searches that produced this cohort have already been paid for; deleting it
          means re-running and re-paying for them if you want it back.
        </p>
      </ActionConfirmModal>
    </div>
  );
}

function statusLabel(s: Cohort['status']): string {
  switch (s) {
    case 'harvested': return 'Awaiting your approval';
    case 'approved': return 'Approved, ready to score';
    case 'scoring': return 'Scoring in progress';
    case 'scored': return 'Scored';
  }
}

/**
 * One results row.
 *
 * ⭐⭐ THE SCORE IS NEVER SHOWN BARE — the contributing tiers sit beside it. Two rules of
 * the rubric combine to make a raw score non-comparable across records: escalation is
 * skipped when a cheaper tier already clears the pass mark, and escalating adds BOTH raw
 * points and a bigger chain-of-evidence bonus. So a weaker record that was forced to buy
 * more evidence can carry a HIGHER number than a stronger one that passed early. Showing
 * "124 (T1+T2)" next to "119 (T1)" is what stops the table being read as a ranking.
 */
function CandidateRow({
  candidate, expanded, onToggle, onReclassify, onReject, onRemove,
}: {
  candidate: Candidate; expanded: boolean; onToggle: () => void;
  onReclassify: (c: Candidate) => void;
  onReject: (c: Candidate) => void;
  onRemove: (c: Candidate) => void;
}) {
  const tone = verdictTone(candidate.verdict);
  return (
    <>
      {/* UAT #2 — the ROW toggles, matching the pattern used elsewhere on the site.
          Keyboard-reachable: a click-only row is unusable without a mouse. */}
      <tr
        className={`growth-row-clickable${expanded ? ' growth-row-expanded' : ''}`}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${candidate.businessName}`}
      >
        <td>
          <div className="growth-cell-primary">{candidate.businessName}</div>
          <div className="growth-cell-secondary">{candidate.domain}</div>
        </td>
        <td>
          <span className={`portal-badge growth-verdict growth-verdict-${tone}`}>
            {verdictLabel(candidate.verdict)}
          </span>
        </td>
        {/* T1 — the free tier, always run. */}
        <td>
          <TierScoreCell
            score={candidate.t1Score ?? null}
            green={candidate.t1Green}
            red={candidate.t1Red}
            unscored={candidate.verdict === 'unscored'}
            showPass
          />
        </td>
        {/* T2 — paid, and only when T1 did not already settle it. */}
        <td>
          {candidate.verdict === 'unscored' ? (
            <span className="growth-cell-secondary">—</span>
          ) : candidate.businessReason === 't1_auto_pass' ? (
            /* ⭐⭐ NOT BLANK. A blank cell reads as "untested"; this record cleared 100 on
               the free tier alone and the paid tier was deliberately never bought. That
               distinction IS the pilot metric. */
            <span className="growth-skipped" title="Cleared 100 on T1 alone — the paid tier was never run">
              skipped — passed on T1
            </span>
          ) : (
            <TierScoreCell
              score={candidate.t2Score ?? null}
              green={candidate.t2Green}
              red={candidate.t2Red}
              unscored={false}
            />
          )}
        </td>
        <td>
          <ScoreCell
            score={candidate.emailScore}
            tiers={candidate.emailTiers}
            unscored={candidate.verdict === 'unscored'}
          />
        </td>
        <td>{pathLabel(candidate.path)}</td>
        <td>
          <div className="growth-cell-primary">{sizeLabel(candidate.sizeBand)}</div>
          {typeof candidate.headcount === 'number' && (
            <div className="growth-cell-secondary">{candidate.headcount} stated</div>
          )}
          {candidate.aggregatorSuspected && (
            /* ⭐ ADVISORY ONLY — §7 makes directory detection best-effort and it never
               excludes. Shown as a signal the operator can sort/filter on and correct via
               reclassification, never as a verdict. */
            <span
              className="growth-agg-flag"
              title={`Possible directory or comparison site (confidence ${candidate.aggregatorConfidence ?? 0}%). Reassign its sector if that is wrong.`}
            >
              <span aria-hidden="true">⚑</span> Possible directory
            </span>
          )}
        </td>
        <td className="portal-users-row-actions portal-actions-col">
          {/* Measured-aligned icon row (34px buttons, 8px gap) — the standard
              multi-action pattern (.portal-actions-row). UAT #3. */}
          <div className="portal-actions-row" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="portal-action-icon-btn"
              onClick={() => onReclassify(candidate)}
              aria-label={`Reclassify ${candidate.businessName}`}
              title="Reassign sector"
            >
              {/* tag glyph — the "reclassify" action */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </button>
            <button
              type="button"
              className="portal-action-icon-btn portal-action-icon-btn-danger"
              onClick={() => onReject(candidate)}
              aria-label={`Reject ${candidate.businessName}`}
              title="Reject — records it in the suspect registry"
            >
              {/* trash glyph — the standard "reject/delete" action */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
            <button
              type="button"
              className="portal-action-icon-btn"
              onClick={() => onRemove(candidate)}
              aria-label={`Remove ${candidate.businessName} from the cohort`}
              title="Remove from cohort — records no judgement"
            >
              {/* minus-circle glyph — "remove", distinct from "reject" */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="growth-detail-row">
          <td colSpan={8}>
            {candidate.noDataGroups.length > 0 && (
              <div className="growth-detail-block">
                {/*
                  ⭐ THE TOOLING-GAP WORK LIST. "3 groups had no data" is a shrug; naming
                  them turns an unscoreable record into something the operator can fix.
                  ⭐⭐ AND THE TIER IS SHOWN, because the no-score fail rule counts
                  ">2 no-data groups" SEPARATELY within T1+T2 (Business) and T3+T4 (Email).
                  Without it, three gaps could be fatal or harmless and the screen looked
                  identical either way — during UAT a T4 group was read as a T1 gap for
                  exactly this reason.
                */}
                <h4>Checks with no data available</h4>
                <p className="growth-detail-help">
                  These could not be run — usually a missing API key rather than anything
                  wrong with the business. More than two <em>within the same score</em>
                  fails that score automatically, because silence is not confidence.
                </p>
                <ul className="growth-gap-list">
                  {/* Sorted by tier (UAT #1) so the Business gaps and the Email gaps read
                      as two blocks — which is how the no-score fail rule counts them. */}
                  {[...candidate.noDataGroups]
                    .sort((a, b) => groupTier(a).localeCompare(groupTier(b))
                      || groupLabel(a).localeCompare(groupLabel(b)))
                    .map((g) => (
                    <li key={g}>
                      <span className={`growth-tier-chip growth-tier-${groupTier(g)}`}>
                        {groupTier(g)}
                      </span>
                      <strong>{groupLabel(g)}</strong>
                      {tierTrack(g) && (
                        <span className="growth-cell-secondary"> · {tierTrack(g)} ACT</span>
                      )}
                      {candidate.notes?.[g] ? ` — ${candidate.notes[g]}` : ''}
                    </li>
                    ))}
                </ul>
              </div>
            )}

            {candidate.notes && (
              <div className="growth-detail-block">
                {/*
                  ⭐⭐ EVERY CHECK THAT RAN, WITH WHAT IT SCORED — the auditability half.
                  A gaps-only view says what went wrong and nothing about whether the checks
                  that DID run produced sensible numbers, so a plausible-but-wrong score is
                  invisible. "Site content and structure 16/20" is checkable; "no gaps" is not.
                */}
                <h4>Checks that ran</h4>
                <table className="growth-detail-table">
                  <thead>
                    <tr><th>Tier</th><th>Check</th><th>Score</th><th>What it found</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(candidate.notes)
                      .filter(([g]) => !candidate.noDataGroups.includes(g))
                      .sort(([a], [b]) => groupTier(a).localeCompare(groupTier(b))
                        || groupLabel(a).localeCompare(groupLabel(b)))
                      .map(([g, note]) => {
                        const pts = candidate.groupScores?.[g];
                        const max = GROUP_MAX[g];
                        return (
                          <tr key={g}>
                            <td>
                              <span className={`growth-tier-chip growth-tier-${groupTier(g)}`}>
                                {groupTier(g)}
                              </span>
                            </td>
                            <td>{groupLabel(g)}</td>
                            <td className="growth-detail-score">
                              {/* A gate or a penalty has no denominator — showing "—/20"
                                  for one would invent a scale it does not have. */}
                              {typeof pts === 'number' && typeof max === 'number'
                                ? `${pts}/${max}`
                                : typeof pts === 'number' ? String(pts) : '—'}
                            </td>
                            <td className="growth-cell-secondary">{note}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <p className="growth-detail-help">
                  This detail is shown for this run only and is not stored — only the
                  outcome and the two scores are kept against the record.
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * A score, its pass/fail against the 100 mark, and the tiers that produced it.
 *
 * ⭐⭐ THE PASS MARKER IS NOT A CONVENIENCE. The send decision is an AND-gate — BOTH scores
 * must independently clear 100 (framework §3, §12) — so a bare pair of numbers makes the
 * operator do the comparison, twice, per row. And because scores from different tier-exits
 * are not comparable magnitudes, "bigger looks better" is actively misleading: the tiers
 * beside the number are what make it readable at all.
 */
/**
 * One TIER's own score, with its green/red counts.
 *
 * Separate from `ScoreCell` (which shows a whole TRACK against the 100 mark) because a
 * tier's number is not a pass/fail on its own — T1 alone clearing 100 is meaningful, but
 * T2 alone clearing it is not a thing. So this shows the tally and the counters, and only
 * T1 carries the pass marker.
 */
function TierScoreCell({
  score, green, red, unscored, showPass = false,
}: {
  score: number | null; green?: number; red?: number;
  unscored: boolean; showPass?: boolean;
}) {
  if (unscored || score === null) return <span className="growth-cell-secondary">—</span>;
  const passed = score >= PASS_MARK;
  return (
    <div className="growth-score-cell">
      <span className={showPass ? `growth-score-mark growth-score-mark-${passed ? 'pass' : 'fail'}` : undefined}>
        {showPass && <span aria-hidden="true">{passed ? '✓' : '✗'}</span>}
        <span className="growth-score">{score}</span>
        {/* ⚠ Colour and a glyph are not enough on their own — the state needs a text
            equivalent, exactly as ScoreCell does. Omitting it here made the T1 column
            convey pass/fail by appearance only. */}
        {showPass && (
          <span className="portal-visually-hidden">
            {passed ? `passes the ${PASS_MARK} mark` : `below the ${PASS_MARK} mark`}
          </span>
        )}
      </span>
      {(typeof green === 'number' || typeof red === 'number') && (
        <div className="growth-cell-secondary" title="Groups scoring ≥80% (green) and <50% (red) of their own max">
          {green ?? 0} green · {red ?? 0} red
        </div>
      )}
    </div>
  );
}

function ScoreCell({
  score, tiers, unscored,
}: { score: number | null; tiers: string[]; unscored: boolean }) {
  if (unscored || score === null) return <span className="growth-cell-secondary">—</span>;
  const passed = score >= PASS_MARK;
  return (
    <div className="growth-score-cell">
      <span className={`growth-score-mark growth-score-mark-${passed ? 'pass' : 'fail'}`}>
        {/* Never colour alone — the glyph and the title carry it for anyone who cannot
            distinguish the red from the green. */}
        <span aria-hidden="true">{passed ? '✓' : '✗'}</span>
        <span className="growth-score">{score}</span>
        <span className="portal-visually-hidden">
          {passed ? `passes the ${PASS_MARK} mark` : `below the ${PASS_MARK} mark`}
        </span>
      </span>
      {tiers.length > 0 && (
        <div className="growth-cell-secondary" title="Which tiers contributed to this score">
          {tiers.join(' + ')}
        </div>
      )}
    </div>
  );
}
